import { absoluteUrl } from "@/lib/site";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SectionHeader } from "@/components/site/SectionHeader";

// Internal build documentation. Intentionally not linked from primary navigation.
export const Route = createFileRoute("/blueprint")({
  component: Blueprint,
  head: () => ({
    meta: [
      { title: "Blueprint · Buddha KA · Internal Build Documentation" },
      { name: "description", content: "Internal build documentation for Buddha KA + KAfrequency." },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/blueprint") }],
  }),
});

function Blueprint() {
  return (
    <article className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-24">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Internal · Not Public Nav</p>
      <h1 className="mt-3 font-serif text-5xl sm:text-6xl">Blueprint</h1>
      <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
        Living documentation for the Buddha KA + KAfrequency digital ecosystem. This page is excluded from the public sitemap and search.
      </p>

      {/* PRD */}
      <Section id="prd" eyebrow="1 · PRD" title="Product requirements — Phase 1.">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li>Cinematic mobile-first site unifying music, consciousness, inner-technology, and cultural institution.</li>
          <li>Positioning: Music for the awakening. Systems for the becoming.</li>
          <li>Preserve obsidian / charcoal / gold / ivory / emerald / violet palette. Editorial serif + tight sans. Restrained motion.</li>
          <li>No live commerce, no live payments, no live newsletter delivery, no fabricated dates or streaming URLs.</li>
          <li>All missing facts render as clearly labeled <code className="text-gold">[Add ...]</code> placeholders.</li>
        </ul>
      </Section>

      {/* Sitemap */}
      <Section id="sitemap" eyebrow="2 · Sitemap" title="Complete route map.">
        <ul className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
          {[
            "/  · Home portal",
            "/music · Archive with search & filters",
            "/music/$slug · Release world (Golden Lotus featured)",
            "/activations · Category-filtered archive",
            "/activations/$slug · Activation detail + breath timer",
            "/wisdom · Editorial reading room",
            "/wisdom/$slug · Article detail",
            "/visuals · Categorized visual archive",
            "/books · Book grid",
            "/books/$slug · Book detail",
            "/about · Philosophy, timeline, press bio",
            "/contact · Multi-inquiry form",
            "/community · Membership tiers",
            "/press · Media kit (placeholder)",
            "/search · Global search (Phase 2)",
            "/newsletter · Confirmation flow (Phase 2)",
            "/shop, /shop/$slug · Phase 2 placeholders",
            "/account, /admin · Phase 2 placeholders",
            "/privacy, /terms, /refund, /shipping, /accessibility",
            "/blueprint · This document (noindex)",
            "/plan · Legacy plan reference",
            "404 · Premium not-found in __root",
          ].map((r) => (
            <li key={r} className="rounded border hairline bg-charcoal px-3 py-2">{r}</li>
          ))}
        </ul>
      </Section>

      {/* Journeys */}
      <Section id="journeys" eyebrow="3 · Journeys" title="Primary visitor journeys.">
        <ul className="space-y-3 text-muted-foreground">
          <li><strong className="text-ivory">The Listener</strong> — Enters via /, hears featured release, subscribes, explores archive.</li>
          <li><strong className="text-ivory">The Practitioner</strong> — Discovers /activations, runs breath timer at Night Seal, follows daily transmissions.</li>
          <li><strong className="text-ivory">The Reader</strong> — Enters /wisdom, reads &ldquo;Attention Is Sacred Currency&rdquo;, follows related music/activation, subscribes.</li>
          <li><strong className="text-ivory">The Curator</strong> — Reaches /press via /about to request media kit or license.</li>
          <li><strong className="text-ivory">The Partner</strong> — Reaches /contact with Booking / Production / Licensing / Partnership intent.</li>
        </ul>
      </Section>

      {/* Database */}
      <Section id="database" eyebrow="4 · Database" title="Data plan (Phase 2 · Lovable Cloud).">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li><code>profiles</code> — id (auth.users), display_name, avatar_url, tier, created_at.</li>
          <li><code>user_roles</code> — user_id, role (admin/editor/member) with <code>has_role()</code> security-definer.</li>
          <li><code>releases</code>, <code>tracks</code>, <code>streaming_links</code>, <code>credits</code>.</li>
          <li><code>activations</code>, <code>wisdom_posts</code>, <code>books</code>, <code>visuals</code>.</li>
          <li><code>newsletter_subscribers</code> (email, confirmed_at, source).</li>
          <li><code>contact_inquiries</code> (type, message, budget, timeline, consent).</li>
          <li><code>orders</code>, <code>order_items</code>, <code>memberships</code> — Phase 3.</li>
          <li>Every public table: explicit GRANTs + RLS policies; roles read via <code>has_role()</code>.</li>
        </ul>
      </Section>

      {/* Components */}
      <Section id="components" eyebrow="5 · Components" title="Component inventory.">
        <ul className="grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
          {[
            "Navigation · sticky, mobile full-screen dialog",
            "Footer · sitemap, newsletter, platforms",
            "AnkhMark · animated brand glyph",
            "PlaceholderArt · intentional art frame",
            "SectionHeader · editorial eyebrow/title/description",
            "BreathTimer · box / coherent / 4-7-8, a11y controls",
            "ComingSoon · templated empty state",
            "Streaming buttons · disabled+locked when URL absent",
            "Timeline · gold-anchored vertical list",
            "Release/Activation/Wisdom cards",
          ].map((c) => (
            <li key={c} className="rounded border hairline bg-charcoal px-3 py-2">{c}</li>
          ))}
        </ul>
      </Section>

      {/* Design tokens */}
      <Section id="design" eyebrow="6 · Design System" title="Tokens & principles.">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li>Palette: obsidian (background), charcoal (surface), ivory (text), gold + gold-luminous (accent gradient), emerald + violet (secondary highlights).</li>
          <li>Type: Cormorant Garamond (serif display), Inter Tight (sans body).</li>
          <li>Motion: <code>animate-halo</code>, <code>animate-frequency</code>, <code>animate-rise</code>. Reduced-motion honored via tw-animate-css.</li>
          <li>Framing: hairline borders, gold restraint (used as accent — never full flood), halo backgrounds at low opacity.</li>
          <li>Rings: subtle frequency circles behind heroes, never carnival-mystic.</li>
          <li>Never hardcode colors — always design tokens from <code>src/styles.css</code>.</li>
        </ul>
      </Section>

      {/* Phased plan */}
      <Section id="phases" eyebrow="7 · Phases" title="Implementation roadmap.">
        <div className="space-y-4 text-muted-foreground">
          <div><strong className="text-ivory">Phase 1 · Foundation (current):</strong> Static premium site. All routes reachable. Breath timer functional. Content seeded. No backend.</div>
          <div><strong className="text-ivory">Phase 2 · Cloud &amp; Auth:</strong> Lovable Cloud enabled. Auth, roles, tables + RLS. Contact + newsletter server functions (Resend). Global search. Persistent audio player. Admin dashboard.</div>
          <div><strong className="text-ivory">Phase 3 · Commerce:</strong> Books, artifacts, memberships. Stripe integration. Order management. Shipping/refund policies finalized.</div>
          <div><strong className="text-ivory">Phase 4 · Media &amp; Community:</strong> Video pipeline, private live sessions, Inner Circle downloads.</div>
        </div>
      </Section>

      {/* Assets — the Control Center */}
      <Section id="assets" eyebrow="8 · Asset Control Center" title="Required assets & information.">
        <p className="text-muted-foreground">
          Nothing here is fabricated. Every item below must be supplied by Buddha KA / KAfrequency before it appears publicly.
        </p>
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {ASSET_GROUPS.map((g) => (
            <div key={g.title} className="rounded-xl border hairline bg-charcoal p-5">
              <p className="text-xs uppercase tracking-[0.3em] text-gold">{g.title}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {g.items.map((i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-0.5 text-gold">◆</span>
                    <span>{i}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Section>

      {/* Tech decisions */}
      <Section id="tech" eyebrow="9 · Technical Decisions" title="Critical choices.">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li>Stack: TanStack Start v1, React 19, Vite 7, Tailwind v4, shadcn primitives.</li>
          <li>Routing: file-based under <code>src/routes/</code>; every content section is its own route (SEO-first).</li>
          <li>State: TanStack Query where server data enters (Phase 2+).</li>
          <li>Head metadata: per-route via <code>head()</code>; canonical only at leaf; og:image only when a real asset exists.</li>
          <li>Backend: Lovable Cloud (Supabase-backed) — enable at Phase 2.</li>
          <li>Server functions authoring: <code>createServerFn</code> with <code>requireSupabaseAuth</code> middleware for protected calls.</li>
          <li>Payments: not connected. Recommend evaluation in Phase 3.</li>
          <li>SEO: unique title/description per route, JSON-LD on wisdom posts, sitemap.xml route present.</li>
        </ul>
      </Section>

      {/* Risk & QC */}
      <Section id="risk" eyebrow="10 · Risk & QC" title="Quality control checklist.">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li>No fabricated dates, prices, streaming URLs, testimonials, awards, or metrics.</li>
          <li>Every disabled streaming button carries an aria-label + title explaining pending state.</li>
          <li>Contact form validates client-side; explicit note that secure server submission arrives Phase 2.</li>
          <li>BreathTimer: keyboard-operable Start/Pause/Reset, aria-labels, honors reduced-motion via CSS animation policy.</li>
          <li>Responsive audit: 320 / 375 / 430 / 768 / 1024 / 1440. Multi-item headers use grid + min-w-0 + shrink-0.</li>
          <li>Contrast: uses semantic tokens only (never <code>text-white</code> / <code>bg-black</code>).</li>
          <li>Focus rings on interactive elements; skip-link and single <code>&lt;main&gt;</code> via layout.</li>
          <li>404 present in root route; every loader-bearing route has <code>notFoundComponent</code>.</li>
        </ul>
      </Section>

      {/* Launch */}
      <Section id="launch" eyebrow="11 · Launch QA" title="Pre-publish checklist.">
        <ul className="list-disc space-y-2 pl-6 text-muted-foreground">
          <li>All <code>[Add ...]</code> placeholders replaced with confirmed content.</li>
          <li>Official portrait / KAfrequency symbol / favicon uploaded.</li>
          <li>Cover art wired for Golden Lotus and all released projects.</li>
          <li>Streaming URLs added to <code>STREAMING_LINKS</code> — public buttons upgrade to live links automatically.</li>
          <li>Lovable Cloud enabled; contact + newsletter routed through server functions.</li>
          <li>Legal pages finalized (privacy, terms, refund, shipping, accessibility).</li>
          <li>Domain chosen; canonical URLs promoted from relative to absolute where required.</li>
          <li>Social preview image (og:image) generated per leaf where meaningful — never a placeholder.</li>
        </ul>
      </Section>

      <div className="mt-16 flex flex-wrap gap-3 border-t hairline pt-8">
        <Link to="/" className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">Return home</Link>
        <Link to="/plan" className="rounded-full border border-gold px-5 py-2.5 text-sm text-gold hover:bg-gold hover:text-obsidian">Legacy plan doc</Link>
      </div>
    </article>
  );
}

function Section({ id, eyebrow, title, children }: { id: string; eyebrow: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mt-16 scroll-mt-24 border-t hairline pt-12">
      <SectionHeader eyebrow={eyebrow} title={title} />
      <div className="mt-6">{children}</div>
    </section>
  );
}

const ASSET_GROUPS: Array<{ title: string; items: string[] }> = [
  {
    title: "Artist Identity",
    items: [
      "Official Buddha KA hero portrait (high-resolution, print quality)",
      "Optional hero video loop (short, silent-friendly)",
      "Transparent KAfrequency symbol (SVG preferred)",
      "Favicon pack (16, 32, 180, 512 px + maskable)",
    ],
  },
  {
    title: "Music Assets",
    items: [
      "Cover art — Golden Lotus (featured single)",
      "Cover art — Golden Frequency, The Golden Wave, Ascended and Armed",
      "Cover art — The Golden KA, The Siddha Sutra",
      "Official audio preview files (30–90s, MP3/AAC)",
      "Confirmed release dates for each project",
    ],
  },
  {
    title: "Streaming & Social",
    items: [
      "Spotify, Apple Music, YouTube, SoundCloud, Bandcamp URLs",
      "Instagram, TikTok, Facebook handles + URLs",
      "Discord invite (if applicable)",
      "Official contact email (info@ / press@ / booking@)",
    ],
  },
  {
    title: "Books & Systems",
    items: [
      "Cover files — 47 Gates of Consciousness",
      "Cover files — KAfrequency Cognitive Manual",
      "Cover files — The Crystallized Mind",
      "Purchase / download destinations per format",
    ],
  },
  {
    title: "Press",
    items: [
      "Approved press photos (portrait + landscape)",
      "Logo pack (mark + wordmark, light + dark variants)",
      "Long bio + short bio (approved copy)",
      "Licensing terms and press contact",
    ],
  },
  {
    title: "Infrastructure — Phase 2+",
    items: [
      "Newsletter provider decision (Resend recommended) + API key",
      "Domain choice + DNS control",
      "Stripe (Phase 3) — commerce configuration",
      "Analytics posture — provider or opt-out policy",
    ],
  },
];
